import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, FileText, Calendar, Globe, TrendingUp, Award } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Toaster } from '../components/ui/toaster';
import GetInvolvedModal from '../components/GetInvolvedModal';
import SubscribeModal from '../components/SubscribeModal';
import { getNews, getStats } from '../services/api';

const Home = () => {
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState({
    totalMembers: 0,
    countries: 0,
    committees: 0,
    politicalGroups: 0,
    sessionsThisYear: 0,
    billsPassed: 0
  });
  const [loading, setLoading] = useState(true);
  const [showGetInvolved, setShowGetInvolved] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsData, statsData] = await Promise.all([
          getNews({ limit: 6 }),
          getStats()
        ]);
        setNews(newsData);
        setStats(statsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Toaster />
      <GetInvolvedModal isOpen={showGetInvolved} onClose={() => setShowGetInvolved(false)} />
      <SubscribeModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
              United States of Africa.<br/>Kingdom of Kush Youth Parliament
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Empowering the next generation of African leaders to shape policies and drive continental progress
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 font-semibold shadow-lg hover:shadow-cyan-500/50 transition-all">
                <Link to="/members">Meet Our Members <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-white transition-all">
                <Link to="/about">Learn More</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { label: 'Members', value: stats.totalMembers, icon: Users },
              { label: 'Countries', value: stats.countries, icon: Globe },
              { label: 'Committees', value: stats.committees, icon: FileText },
              { label: 'Political Groups', value: stats.politicalGroups, icon: Award },
              { label: 'Sessions 2025', value: stats.sessionsThisYear, icon: Calendar },
              { label: 'Bills Passed', value: stats.billsPassed, icon: TrendingUp },
            ].map((stat, index) => (
              <div key={index} className="text-center text-white">
                <stat.icon className="h-8 w-8 mx-auto mb-2 opacity-90" />
                <div className="text-3xl md:text-4xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Latest News</h2>
            <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all">
              View All News <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.slice(0, 6).map((item) => (
              <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow group border-gray-200">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <Badge className="absolute top-4 left-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 border-0 shadow-lg">
                    {item.category}
                  </Badge>
                </div>
                <CardHeader>
                  <CardDescription className="text-gray-600">{item.date}</CardDescription>
                  <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm line-clamp-3">{item.excerpt}</p>
                  <Button variant="link" className="mt-4 p-0 h-auto text-orange-600 hover:text-orange-700 transition-colors">
                    Read More <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Access Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Quick Access</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Members Directory',
                description: 'Browse our {0} members from across Africa'.replace('{0}', stats.totalMembers),
                link: '/members',
                icon: Users,
                color: 'from-cyan-400 to-blue-600'
              },
              {
                title: 'Plenary Sessions',
                description: 'Watch live and recorded parliamentary sessions',
                link: '/plenary',
                icon: Calendar,
                color: 'from-orange-500 to-orange-600'
              },
              {
                title: 'Committees',
                description: 'Explore our {0} specialized committees'.replace('{0}', stats.committees),
                link: '/committees',
                icon: FileText,
                color: 'from-blue-600 to-indigo-700'
              },
              {
                title: 'Documents',
                description: 'Access legislative documents and reports',
                link: '/documents',
                icon: FileText,
                color: 'from-teal-500 to-cyan-600'
              },
            ].map((item, index) => (
              <Link key={index} to={item.link}>
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-gray-200 group">
                  <CardHeader>
                    <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">{item.title}</CardTitle>
                    <CardDescription className="text-gray-600">{item.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Join the Movement</h2>
          <p className="text-xl text-gray-200 mb-8">
            Be part of shaping Africa's future. Engage with our initiatives and contribute to continental development.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => setShowGetInvolved(true)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 font-semibold shadow-lg hover:shadow-cyan-500/50 transition-all"
            >
              Get Involved
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => setShowSubscribe(true)}
              className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-white transition-all"
            >
              Subscribe to Updates
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;